import React from 'react';
import { saveAs } from 'file-saver';
import { useApolloClient } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { BRAND_RED, COLOR_TEXT, COLOR_WHITE, VANESSA_SHOP_ID } from '../../../constants';
import { GET_OPTION_VALUES } from '../../../graphql/optionValue/query';
import { errorSwal } from '../../../helpers/sweetalert';
import {
    buildOptionColumns,
    EXCEL_COLUMNS,
    EXCEL_SAMPLE_ROWS,
    MAX_OPTIONS_PER_ROW,
    SUB_CATEGORY_COLUMN,
    SUB_CATEGORY_SAMPLE,
} from './productExcelImport';

//* ສີແລະຟອນດຽວກັບໄຟລ໌ Excel ຂອງອໍເດີ (OrderGroupDownload.js)
//* cell ໃຊ້ gradient ຂອງແອັບ (COLOR_APP) ບໍ່ໄດ້ ຈຶ່ງໃຊ້ສີແບຣນດຽວ
//* ຟອນຈະສະແດງຄືແອັບ ຖ້າເຄື່ອງມີ Noto Sans Lao
const EXCEL_FONT = 'Noto Sans Lao';
const toArgb = (hex) => {
    const value = hex.replace('#', '');
    const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
    return `FF${full.toUpperCase()}`;
};
const BORDER_COLOR = 'FFD9D9D9';
const thinBorder = ['top', 'left', 'bottom', 'right'].reduce((border, side) => ({
    ...border,
    [side]: { style: 'thin', color: { argb: BORDER_COLOR } },
}), {});

const OPTION_SHEET = 'Options';
const OPTION_INPUT_ROWS = 1000; //* ເທົ່າກັບຈຳນວນແຖວສູງສຸດທີ່ນຳເຂົ້າໄດ້

const columnLetter = (index) => {
    let number = index + 1;
    let letters = '';
    while (number > 0) {
        const remainder = (number - 1) % 26;
        letters = String.fromCharCode(65 + remainder) + letters;
        number = Math.floor((number - 1) / 26);
    }
    return letters;
};

const DownloadExcelButton = () => {
    const apolloClient = useApolloClient();

    //* ຄໍລຳປະເພດຍ່ອຍ ແລະ option ເປີດສະເພາະຮ້ານ Vanessa ກ່ອນ — ຮ້ານອື່ນໄດ້ 12 ຄໍລຳຫຼັກຄືເກົ່າ
    const fetchOptionColumns = async () => {
        const shop = localStorage.getItem('SHOP');
        const { data } = await apolloClient.query({
            query: GET_OPTION_VALUES,
            variables: { where: { shop, isDeleted: false }, orderBy: 'createdAt_ASC', limit: 5000 },
            fetchPolicy: 'network-only',
        });
        return buildOptionColumns(data?.optionValues?.data);
    };

    const handleDownload = async () => {
        try {
            const isVanessaShop = VANESSA_SHOP_ID.includes(localStorage.getItem('SHOP'));
            const optionColumns = isVanessaShop ? await fetchOptionColumns() : [];
            const extraColumns = isVanessaShop ? [SUB_CATEGORY_COLUMN] : [];
            const firstOptionIndex = EXCEL_COLUMNS.length + extraColumns.length;
            //* ປະເພດຍ່ອຍຕໍ່ຈາກປະເພດ — ຕົວນຳເຂົ້າອ່ານຕາມຊື່ຫົວຄໍລຳ ຕຳແໜ່ງຈຶ່ງບໍ່ມີຜົນຕໍ່ການອ່ານ
            const afterCategory = EXCEL_COLUMNS.findIndex((column) => column.key === 'category') + 1;
            const allColumns = [
                ...EXCEL_COLUMNS.slice(0, afterCategory),
                ...extraColumns,
                ...EXCEL_COLUMNS.slice(afterCategory),
                ...optionColumns,
            ];
            //* ໂຫຼດຕອນກົດເທົ່ານັ້ນ ບໍ່ໃຫ້ library ຂະໜາດໃຫຍ່ເຂົ້າໄປໃນ bundle ຫຼັກ
            const exceljsModule = await import('exceljs');
            const ExcelJS = exceljsModule.default || exceljsModule;

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Sheet1', { views: [{ state: 'frozen', ySplit: 1 }] });
            sheet.columns = allColumns.map(({ key, width }) => ({ key, width }));

            const headerRow = sheet.getRow(1);
            allColumns.forEach((column, index) => {
                const cell = headerRow.getCell(index + 1);
                cell.value = column.header;
                cell.font = { name: EXCEL_FONT, size: 11, bold: true, color: { argb: toArgb(COLOR_WHITE) } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(BRAND_RED) } };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = thinBorder;
            });
            headerRow.height = 32;

            EXCEL_SAMPLE_ROWS.forEach((sample, rowIndex) => {
                const row = sheet.getRow(rowIndex + 2);
                allColumns.forEach((column, index) => {
                    const cell = row.getCell(index + 1);
                    const optionIndex = index - firstOptionIndex;
                    //* EXCEL_SAMPLE_ROWS ລຽງຕາມ EXCEL_COLUMNS ຈຶ່ງຕ້ອງຫາດ້ວຍຄໍລຳ ບໍ່ແມ່ນຕຳແໜ່ງໃນໄຟລ໌
                    let value = sample[EXCEL_COLUMNS.indexOf(column)];
                    //* ຕົວຢ່າງແຖວທຳອິດໃສ່ປະເພດຍ່ອຍ ແລະ ຄ່າ option ຈິງຂອງຮ້ານ (ບໍ່ເກີນຈຳນວນທີ່ອະນຸຍາດຕໍ່ແຖວ)
                    if (column === SUB_CATEGORY_COLUMN) {
                        value = rowIndex === 0 ? SUB_CATEGORY_SAMPLE : '';
                    } else if (optionIndex >= 0) {
                        value = rowIndex === 0 && optionIndex < MAX_OPTIONS_PER_ROW ? optionColumns[optionIndex].values[0] : '';
                    }
                    cell.value = value === '' ? null : value;
                    cell.font = { name: EXCEL_FONT, size: 11 };
                    cell.alignment = { vertical: 'middle' };
                    cell.border = thinBorder;
                });
            });

            if (optionColumns.length > 0) {
                //* ຊີດທີ 2 = ຄ່າທີ່ເລືອກໄດ້ຂອງຮ້ານ ແລະ ເປັນແຫຼ່ງຂອງ dropdown (ຕົວນຳເຂົ້າອ່ານສະເພາະຊີດທຳອິດ)
                const optionSheet = workbook.addWorksheet(OPTION_SHEET);
                optionColumns.forEach((column, index) => {
                    optionSheet.getColumn(index + 1).width = column.width;
                    const head = optionSheet.getCell(1, index + 1);
                    head.value = column.optionName;
                    head.font = { name: EXCEL_FONT, size: 11, bold: true, color: { argb: toArgb(COLOR_WHITE) } };
                    head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(BRAND_RED) } };
                    column.values.forEach((value, valueIndex) => {
                        const cell = optionSheet.getCell(valueIndex + 2, index + 1);
                        //* ຕ້ອງເປັນຂໍ້ຄວາມທັງສອງຝັ່ງ: ຄ່າ "36" ໃນລາຍການເປັນຂໍ້ຄວາມ ແຕ່ຕອນເລືອກ ໂປຣແກຣມ
                        //* ຈະແປງເປັນຕົວເລກ ແລ້ວເທຍບບໍ່ກົງກັນ ຈຶ່ງຖືກປະຕິເສດທັງທີ່ເລືອກຈາກລາຍການ
                        cell.numFmt = '@';
                        cell.value = value;
                        cell.font = { name: EXCEL_FONT, size: 11 };
                    });
                    const letter = columnLetter(firstOptionIndex + index);
                    const source = columnLetter(index);
                    const validation = {
                        type: 'list',
                        allowBlank: true,
                        formulae: [`${OPTION_SHEET}!$${source}$2:$${source}$${column.values.length + 1}`],
                        //* ເປັນຕົວຊ່ວຍເລືອກເທົ່ານັ້ນ ບໍ່ບລັອກ: Excel ແລະ Google Sheets ທຽບຄ່າຕ່າງກັນ
                        //* ໜ້ານຳເຂົ້າກວດຄ່າຈິງກັບ option ຂອງຮ້ານອີກຮອບຢູ່ແລ້ວ
                        showErrorMessage: false,
                    };
                    //* ກຳນົດເປັນຊ່ວງດຽວ ຫ້າມຕັ້ງ cell.dataValidation ທີລະຊ່ອງ: exceljs ລວມຊ່ວງຜິດ
                    //* (ໄດ້ M10:M1001 ຊ້ອນກັບ M2:M1001) ຊຶ່ງ Excel ອາດເຫັນວ່າໄຟລ໌ເສຍ
                    sheet.dataValidations.add(`${letter}2:${letter}${OPTION_INPUT_ROWS + 1}`, validation);
                    for (let rowNumber = 2; rowNumber <= OPTION_INPUT_ROWS + 1; rowNumber += 1) {
                        sheet.getCell(`${letter}${rowNumber}`).numFmt = '@';
                    }
                });
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const file = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(file, 'bbbb-sample-file.xlsx');
        } catch (error) {
            console.log('download excel template error: ', error);
            errorSwal('ດາວໂຫຼດໄຟລ໌ຕົວຢ່າງບໍ່ສຳເລັດ');
        }
    };

    return (
        <Button onClick={handleDownload} style={{ borderColor: COLOR_TEXT, color: COLOR_TEXT, background: 'transparent', fontWeight: 'bold', whiteSpace: 'nowrap' }}>ຮູບແບບຕົວຢ່າງ Excel</Button>
    );
};

export default DownloadExcelButton;
